import '@/utils/temporal-polyfill';

import DashingUI from '@dashhudson/dashing-ui';
import { datadogLogs } from '@datadog/browser-logs';
import { createGtm } from '@gtm-support/vue-gtm';
import { PiniaColada } from '@pinia/colada';
import { PiniaColadaRetry } from '@pinia/colada-plugin-retry';
import { createHead } from '@unhead/vue/client';
import mixpanel from 'mixpanel-browser';
import { createApp } from 'vue';

import { getHttpErrorStatus } from '@/apis/axios.utils';
import App from '@/App.vue';
import DragMove from '@/directives/drag-move';
import DragSelect from '@/directives/drag-select';
import InfiniteScroll from '@/directives/infinite-scroll';
import LineClampDirective from '@/directives/line-clamp';
import SlideSelect from '@/directives/slide-select';
import { env } from '@/env';
import AdobeExpressEmbedPlugin from '@/plugins/adobe-express-embed';
import FloatingVuePlugin from '@/plugins/floating-vue';
import SanitizeHtmlPlugin from '@/plugins/sanitize-html';
import router from '@/router-lifecycle';
import { useFlagStore } from '@/stores/flag';
import pinia from '@/stores/pinia';
import setupDayJsPlugins from '@/utils/dayjsExtensions';
import { getUserTimezone } from '@/utils/timezone';

setupDayJsPlugins();

const app = createApp(App);
app.use(pinia);
app.use(PiniaColada, {
  plugins: [
    PiniaColadaRetry({
      retry: (failureCount, error) => {
        const status = getHttpErrorStatus(error);
        if (status) {
          // Retry 429 (rate limit) and 5xx (server errors)
          if (status === 429 || status >= 500) {
            return failureCount < 3;
          }
          // Don't retry other 4xx client errors
          return false;
        }
        // Retry network errors (no response)
        return failureCount < 3;
      },
      delay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
    }),
  ],
  queryOptions: {
    // Data is fresh for 5 minutes (won't refetch if navigating back quickly)
    staleTime: 5 * 60 * 1000,
    // Keep inactive query data for 30 minutes before garbage collection
    // This ensures data survives component unmount during navigation
    gcTime: 30 * 60 * 1000,
    // Refetch when data is stale and component mounts
    refetchOnMount: true,
    // Don't refetch just because window regained focus
    refetchOnWindowFocus: false,
    // Don't refetch on reconnect
    refetchOnReconnect: false,
  },
});
app.use(router);
app.use(FloatingVuePlugin);
app.use(SanitizeHtmlPlugin);
app.use(AdobeExpressEmbedPlugin);
app.use(DashingUI, {
  timezone: () => {
    const flagStore = useFlagStore(pinia);
    return flagStore.ready && flagStore.flags.dashingUiTimezone ? getUserTimezone() : undefined;
  },
  imageApi: {
    prodUrl: import.meta.env.VITE_PROD_IMAGE_API_URL || 'https://images.dashsocial.com',
    devUrl: import.meta.env.VITE_DEV_IMAGE_API_URL || 'https://images.dhdev.co',
    oldDevUrl: import.meta.env.VITE_OLD_DEV_IMAGE_API_URL || 'https://images.dhdev.io',
  },
  datadog: datadogLogs,
  mixpanel,
  globalContext: {
    app: 'dash-web',
    version: env.revision,
  },
  logger: {
    alwaysUseConsole: !!env.isLocalDev,
    consolePrefix: '',
  },
});
app.directive('drag-move', DragMove);
app.directive('drag-select', DragSelect);
app.directive('slide-select', SlideSelect);
app.directive('line-clamp', LineClampDirective);
app.directive('infinite-scroll', InfiniteScroll);

app.use(
  createGtm({
    id: env.gtmId,
    vueRouter: router,
    enabled: env.gtmEnabled,
  }),
);

const head = createHead();
app.use(head);

router.isReady().then(() => app.mount('#app'));
